// Version: 1.1
// v1.1: Computers added in Action1 since the daily sync are listed too. v1.0 only
//       overlaid live status on computers already in the snapshot, so a new PC
//       never appeared until the next sync even though the tab said it was live.
//       New ones carry pending_sync (no patch data yet, not counted as
//       non-compliant) with IP, OS and user from the live call (app.py v4.55);
//       the source line says how many are new.
// v1.0: Live Action1 status for the Computers tab (Client Management and Client Detail).
//       The daily 04:00 snapshot holds patch data; this overlays each computer's
//       current status and last seen time from GET /api/clients/<slug>/action1/live
//       (app.py v4.36), refreshed every 2 minutes while the tab is open.
import { useEffect, useState } from 'react';
import { authFetch } from './auth';

export const A1_STATUS_COLOR = {
  Connected: '#30d158', Online: '#30d158',
  Disconnected: '#e6b800', Offline: '#86868b',
};

export const A1_PENDING_COLOR = '#86868b';

const UK = { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' };

export function fmtA1Time(value) {
  if (!value) return '';
  const d = new Date(String(value).includes('T') ? value : String(value).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-GB', UK) + ' GMT';
}

// Returns { endpoints, live, newCount }. endpoints is the snapshot with live status
// and last_seen overlaid, followed by any computers Action1 has that the snapshot
// does not (pending_sync: true). live is the /action1/live response (or null).
export function useAction1Live(slug, snapshotEndpoints) {
  const [live, setLive] = useState(null);

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    const load = () => {
      authFetch(`/api/clients/${slug}/action1/live`)
        .then(r => r.json())
        .then(d => { if (!cancelled) setLive(d); })
        .catch(() => { if (!cancelled) setLive({ available: false, reason: 'Action1 did not answer' }); });
    };
    load();
    const timer = setInterval(load, 120000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [slug]);

  const map = live?.available ? (live.endpoints || {}) : null;
  const snapshot = snapshotEndpoints || [];
  const known = new Set(snapshot.map(ep => ep.id));
  const merged = snapshot.map(ep => {
    const l = map && map[ep.id];
    return l ? { ...ep, status: l.status || ep.status, last_seen: l.last_seen || ep.last_seen, status_live: true } : ep;
  });
  const added = map
    ? Object.entries(map)
        .filter(([id]) => !known.has(id))
        .map(([id, l]) => ({
          id,
          name: l.name || 'Unknown',
          ip: l.ip || '',
          os: l.os || '',
          user: l.user || '',
          status: l.status || '',
          last_seen: l.last_seen || '',
          status_live: true,
          pending_sync: true,
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    : [];
  return { endpoints: [...merged, ...added], live, newCount: added.length };
}

// Compliance label and colour for a device card; new computers have no patch data yet.
export function a1Compliance(ep) {
  if (ep.pending_sync) return { label: 'New, patch data at the next sync', color: A1_PENDING_COLOR };
  return ep.ce_patch_compliant
    ? { label: '✓ Compliant', color: '#30d158' }
    : { label: '✗ Non-compliant', color: '#ff2d55' };
}

// One line saying where the status shown came from.
export function Action1StatusSource({ live, scannedAt, newCount = 0 }) {
  const style = { fontSize: 11, color: 'var(--text-muted)', margin: '0 0 10px' };
  if (!live) return <div style={style}>Checking live status with Action1…</div>;
  if (live.available) {
    return (
      <div style={style}>
        Status live from Action1 · Updated {fmtA1Time(live.fetched_at)}
        {newCount > 0 ? ` · ${newCount} computer${newCount !== 1 ? 's' : ''} new since the daily sync` : ''}
      </div>
    );
  }
  return (
    <div style={style}>
      Status from the daily sync{scannedAt ? ` · Updated ${fmtA1Time(scannedAt)}` : ''}
      {live.reason ? ` (live status unavailable: ${live.reason})` : ''}
    </div>
  );
}
